import React, { useEffect, useMemo, useState } from 'react';
import { API, authFetch } from './api';

const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n == null ? '—' : `${Number(n)}%`);

const HORIZON = [
  ['lapsed', 'Lapsed', 'text-red-300'],
  ['within_30', 'Within 30 days', 'text-amber-300'],
  ['within_60', 'Within 60 days', 'text-yellow-200'],
  ['within_90', 'Within 90 days', 'text-[#B0BEC5]'],
];

const Stat = ({ label, value, accent }) => (
  <div className="rounded-xl border border-[#546E7A]/30 bg-[#1A2744] p-4">
    <div className="text-xs text-[#B0BEC5]">{label}</div>
    <div className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</div>
  </div>
);

// Section 9.4 — SUPER-ADMIN ONLY report of what AiDA earns across all associations.
// The AiDA revenue share % is super-admin-only and appears nowhere in association-facing UI.
export default function AiDARevenueReport() {
  const [data, setData] = useState(null);
  const [aging, setAging] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    authFetch(`${API}/api/associations/reports/aida-revenue`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Not authorized'))))
      .then(setData).catch((e) => setError(e.message));
    authFetch(`${API}/api/associations/reports/associations-aging`)
      .then((r) => (r.ok ? r.json() : null)).then(setAging).catch(() => {});
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return needle
      ? data.associations.filter((a) => (a.display_name || a.name || '').toLowerCase().includes(needle))
      : data.associations;
  }, [data, q]);

  const exportCsv = () => {
    if (!data) return;
    const head = ['Association', 'Flat fee ($/mo)', 'AiDA share %', 'Seat revenue base ($)', 'Seat share ($/mo)', 'Combined MRR ($/mo)'];
    const lines = [head.join(',')];
    for (const a of data.associations) {
      const cells = [a.display_name || a.name, a.flat_fee, a.share_pct ?? '', a.seat_revenue_base, a.seat_share, a.combined_mrr];
      lines.push(cells.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));
    }
    lines.push(['TOTAL', data.totals.flat_fee, '', '', data.totals.seat_share, data.totals.combined_mrr]
      .map((c) => `"${c}"`).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `aida-revenue-${data.period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return <div className="min-h-screen bg-[#0A0E27] pt-8"><div className="max-w-7xl mx-auto px-3 sm:px-6">
      <p className="text-red-400">{error}</p></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h1 className="text-white text-2xl sm:text-3xl font-bold">AiDA Revenue</h1>
          <button onClick={exportCsv} disabled={!data}
            className="px-4 py-2 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold text-sm disabled:opacity-50">
            Export CSV
          </button>
        </div>
        <p className="text-[#B0BEC5] text-sm mb-6">
          What AiDA earns across all associations{data ? ` · ${data.period}` : ''}. Flat contract fees plus the
          computed seat-share (authorized seat caps of active, billable brokerages × per-seat price × each
          association's AiDA revenue share %). Visible to Super Admins only.
        </p>

        {!data ? (
          <p className="text-[#B0BEC5]">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <Stat label="Flat fees (MRR)" value={money(data.totals.flat_fee)} />
              <Stat label="Seat-share (MRR)" value={money(data.totals.seat_share)} accent="text-[#00E676]" />
              <Stat label="Combined MRR" value={money(data.totals.combined_mrr)} accent="text-[#00D4FF]" />
            </div>

            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-white font-semibold">By Association</h2>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…"
                  className="px-3 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm w-40" />
              </div>

              {/* desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#B0BEC5] border-b border-[#546E7A]/30">
                      <th className="py-2 font-medium">Association</th>
                      <th className="py-2 font-medium text-right">Flat fee</th>
                      <th className="py-2 font-medium text-right">Share %</th>
                      <th className="py-2 font-medium text-right">Seat base</th>
                      <th className="py-2 font-medium text-right">Seat share</th>
                      <th className="py-2 font-medium text-right">Combined MRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.association_id} className="border-b border-[#546E7A]/15">
                        <td className="py-2 text-white break-words">{a.display_name || a.name}
                          {!a.has_contract && <span className="ml-2 text-xs text-gray-500">(no contract)</span>}</td>
                        <td className="py-2 text-right text-white">{money(a.flat_fee)}</td>
                        <td className="py-2 text-right text-[#B0BEC5]">{pct(a.share_pct)}</td>
                        <td className="py-2 text-right text-[#B0BEC5]">{money(a.seat_revenue_base)}</td>
                        <td className="py-2 text-right text-[#00E676]">{money(a.seat_share)}</td>
                        <td className="py-2 text-right text-[#00D4FF] font-semibold">{money(a.combined_mrr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* mobile stacked cards */}
              <div className="md:hidden space-y-2">
                {rows.map((a) => (
                  <div key={a.association_id} className="rounded-lg border border-[#546E7A]/25 bg-[#0A0E27]/40 p-3">
                    <div className="flex justify-between gap-2 mb-1">
                      <span className="text-white font-medium break-words min-w-0">{a.display_name || a.name}</span>
                      <span className="text-[#00D4FF] font-semibold shrink-0">{money(a.combined_mrr)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 text-xs text-[#B0BEC5]">
                      <span>Flat: <span className="text-white">{money(a.flat_fee)}</span></span>
                      <span>Share: <span className="text-white">{pct(a.share_pct)}</span></span>
                      <span>Seat base: <span className="text-white">{money(a.seat_revenue_base)}</span></span>
                      <span>Seat share: <span className="text-[#00E676]">{money(a.seat_share)}</span></span>
                    </div>
                  </div>
                ))}
              </div>
              {rows.length === 0 && <p className="text-[#B0BEC5] text-sm">No associations match.</p>}
            </div>

            {data.trend.length > 0 && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 mb-6">
                <h2 className="text-white font-semibold mb-3">Trend (from invoiced periods)</h2>
                <div className="space-y-1">
                  {data.trend.map((t) => (
                    <div key={t.period} className="flex justify-between text-sm">
                      <span className="text-[#B0BEC5]">{t.period}</span>
                      <span className="text-white">
                        flat {money(t.flat_fee)} · seat <span className="text-[#00E676]">{money(t.seat_share)}</span>
                        {' · '}<span className="text-[#00D4FF] font-semibold">{money(t.combined_mrr)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {aging && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
                <h2 className="text-white font-semibold mb-1">Association Renewal Horizon</h2>
                <p className="text-xs text-[#B0BEC5] mb-3">AiDA-contract renewals coming due across all associations.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {HORIZON.map(([key, label, cls]) => {
                    const list = aging.renewal_horizon[key] || [];
                    return (
                      <div key={key} className="rounded-lg border border-[#546E7A]/25 bg-[#0A0E27]/40 p-3">
                        <div className={`text-xs font-semibold ${cls}`}>{label}</div>
                        <div className="text-2xl text-white font-bold">{list.length}</div>
                        {list.slice(0, 4).map((x) => (
                          <div key={x.association_id} className="text-xs text-[#B0BEC5] truncate">{x.display_name}</div>
                        ))}
                        {list.length > 4 && <div className="text-xs text-gray-500">+{list.length - 4} more</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
